import {
  createAlertLeader,
  type AlertChannelFactory,
  type AlertLeader,
  type AlertLeaderSnapshot,
  type AlertLockManager,
} from './alert-leader';
import { createAlertMute, isMuted, type AlertMute } from './alert-mute';
import type { AlertSource, AlertSourceNotification, AlertSourceState } from './alert-sources';
import {
  installHandoffAudioUnlock,
  isHandoffAudioUnlocked,
  onHandoffAudioUnlock,
  playHandoffAlert,
  unlockHandoffAudio,
} from './handoff-alert-sound';

/* ==========================================================================
   O que decide o som, o título e a notificação dos alertas (D19)

   Fora do React de propósito: o `ConversationAlerts` só entrega o estado de
   cada fonte (`setSourceState`) e lê o retrato (`getSnapshot`, pelo
   `useSyncExternalStore`). Aqui ficam a aba líder, o silêncio, a linha de
   base das pendências, a repetição de 60 s e o título, sem estado de
   componente atualizado dentro de efeito.

   - Pendência nova (a `latestMark` de uma fonte passa a maior já vista): a
     líder sem silêncio toca uma vez e notifica, se a aba estiver oculta ou
     sem foco; a repetição recomeça a contar dali. Assumir a conversa mais
     nova, com outra mais antiga ainda pendente, baixa a marca e não toca.
   - A primeira leitura de cada fonte é linha de base: abrir o painel não toca
     o que já estava pendente (a repetição cuida disso em 60 s).
   - A repetição roda só na líder, sem silêncio, enquanto alguma fonte tem
     contagem; para em 0. A fonte de aviso de uma vez (`repeats: false`, o
     pagamento trocado na rua) toca só na chegada e não entra na repetição.
   - O título ganha "(N) " com a soma das fontes e volta ao original em 0 e
     no `stop`.
   ========================================================================== */

export const ALERT_REPEAT_MS = 60_000;

const TITLE_PREFIX = /^\(\d+\+?\) /;

export type AlertNotificationPermission = NotificationPermission | 'unsupported';

export interface AlertLine {
  sourceId: string;
  state: AlertSourceState;
}

export interface AlertSnapshot {
  /** As fontes com pendência, na ordem do registro. */
  lines: readonly AlertLine[];
  total: number;
  isLeader: boolean;
  /** Alguma aba viva liberou o som; sem nenhuma, a faixa mostra "Ativar som". */
  anyAudioUnlocked: boolean;
  /** Fim do silêncio, quando silenciado agora. */
  mutedUntil: number | null;
  notificationPermission: AlertNotificationPermission;
}

export interface AlertControllerOptions {
  tenantId: string;
  sources: readonly AlertSource[];
  channelFactory?: AlertChannelFactory;
  /** A Web Locks API da aba líder; `navigator.locks` por padrão. */
  locks?: AlertLockManager | null;
  now?: () => number;
  /** Abre a pendência do clique na notificação. */
  open?: (href: string) => void;
}

export interface AlertController {
  start(): void;
  stop(): void;
  setSourceState(sourceId: string, state: AlertSourceState | null): void;
  setOpenHandler(open: (href: string) => void): void;
  /** "Ativar som": libera o áudio desta aba e toca uma vez. */
  enableSound(): void;
  /** "Silenciar por 30 min". */
  mute(): void;
  /** "Ativar notificações": só no clique. */
  requestNotifications(): Promise<void>;
  subscribe(listener: () => void): () => void;
  getSnapshot(): AlertSnapshot;
}

function readNotificationPermission(): AlertNotificationPermission {
  try {
    return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
  } catch {
    return 'unsupported';
  }
}

function tabHiddenOrUnfocused(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.visibilityState === 'hidden') return true;
  try {
    return !document.hasFocus();
  } catch {
    return false;
  }
}

function sameState(a: AlertSourceState | null | undefined, b: AlertSourceState | null): boolean {
  if (!a || !b) return (a ?? null) === b;
  return JSON.stringify(a) === JSON.stringify(b);
}

export function createAlertController(options: AlertControllerOptions): AlertController {
  const now = options.now ?? (() => Date.now());
  const states = new Map<string, AlertSourceState | null>();
  /** A maior marca já vista por fonte (nula enquanto ela não teve pendência). */
  const seenMarks = new Map<string, number | null>();
  const listeners = new Set<() => void>();
  let open = options.open ?? (() => undefined);
  let leader: AlertLeader | null = null;
  let mute: AlertMute | null = null;
  let leaderSnapshot: AlertLeaderSnapshot = { isLeader: true, anyAudioUnlocked: isHandoffAudioUnlocked(), coordinated: false };
  let mutedUntil: number | null = null;
  let permission = readNotificationPermission();
  let repeatTimer: ReturnType<typeof setInterval> | null = null;
  let muteTimer: ReturnType<typeof setTimeout> | null = null;
  let baseTitle: string | null = null;
  let cleanups: Array<() => void> = [];
  let started = false;
  let snapshot: AlertSnapshot = buildSnapshot();

  function activeLines(): AlertLine[] {
    const lines: AlertLine[] = [];
    for (const source of options.sources) {
      const state = states.get(source.id);
      if (state && state.count > 0) lines.push({ sourceId: source.id, state });
    }
    return lines;
  }

  /** As linhas das fontes que repetem o som; o aviso de uma vez só (`repeats: false`) fica fora. */
  function repeatingLines(): AlertLine[] {
    return activeLines().filter((line) => options.sources.find((s) => s.id === line.sourceId)?.repeats !== false);
  }

  function muted(): boolean {
    return isMuted(mutedUntil, now());
  }

  function buildSnapshot(): AlertSnapshot {
    const lines = activeLines();
    return {
      lines,
      total: lines.reduce((sum, line) => sum + line.state.count, 0),
      isLeader: leaderSnapshot.isLeader,
      anyAudioUnlocked: leaderSnapshot.anyAudioUnlocked,
      mutedUntil: muted() ? mutedUntil : null,
      notificationPermission: permission,
    };
  }

  function canSound(): boolean {
    return started && leaderSnapshot.isLeader && !muted();
  }

  function play(sourceId: string | undefined): void {
    const source = options.sources.find((s) => s.id === sourceId);
    if (!source) return;
    try {
      (source.sound ?? playHandoffAlert)();
    } catch {
      // Som que falha não derruba a faixa.
    }
  }

  function notify(notification: AlertSourceNotification): void {
    if (readNotificationPermission() !== 'granted' || !tabHiddenOrUnfocused()) return;
    try {
      const shown = new Notification(notification.title, { body: notification.body, tag: notification.tag });
      shown.onclick = () => {
        try {
          window.focus();
        } catch {
          // Janela que não aceita foco: a navegação basta.
        }
        open(notification.href);
        shown.close();
      };
    } catch {
      // Navegador que recusa criar a notificação fora de um service worker.
    }
  }

  function stopRepeat(): void {
    if (repeatTimer !== null) clearInterval(repeatTimer);
    repeatTimer = null;
  }

  /** Recomeça a repetição (ou a para) conforme a líder, o silêncio e as pendências. */
  function scheduleRepeat(restart: boolean): void {
    const should = canSound() && repeatingLines().length > 0;
    if (!should) {
      stopRepeat();
      return;
    }
    if (repeatTimer !== null && !restart) return;
    stopRepeat();
    repeatTimer = setInterval(() => {
      if (!canSound()) return;
      play(repeatingLines()[0]?.sourceId);
    }, ALERT_REPEAT_MS);
  }

  function syncTitle(total: number): void {
    if (typeof document === 'undefined') return;
    if (total > 0) {
      if (baseTitle === null) baseTitle = document.title.replace(TITLE_PREFIX, '');
      document.title = `(${total}) ${baseTitle}`;
    } else if (baseTitle !== null) {
      document.title = baseTitle;
      baseTitle = null;
    }
  }

  function scheduleMuteEnd(): void {
    if (muteTimer !== null) clearTimeout(muteTimer);
    muteTimer = null;
    if (!started || !muted() || mutedUntil === null) return;
    muteTimer = setTimeout(() => {
      muteTimer = null;
      emit();
      scheduleRepeat(false);
    }, Math.max(0, mutedUntil - now()) + 50);
  }

  function emit(): void {
    snapshot = buildSnapshot();
    if (started) syncTitle(snapshot.total);
    for (const listener of listeners) listener();
  }

  function onLeader(next: AlertLeaderSnapshot): void {
    leaderSnapshot = next;
    emit();
    scheduleRepeat(false);
  }

  function onMuted(until: number | null): void {
    mutedUntil = until;
    emit();
    scheduleRepeat(false);
    scheduleMuteEnd();
  }

  return {
    start() {
      if (started) return;
      started = true;
      leader = createAlertLeader({
        tenantId: options.tenantId,
        audioUnlocked: isHandoffAudioUnlocked,
        channelFactory: options.channelFactory,
        locks: options.locks,
      });
      mute = createAlertMute({ leader });
      leaderSnapshot = leader.snapshot();
      mutedUntil = mute.mutedUntil();
      const currentLeader = leader;
      cleanups = [
        currentLeader.subscribe(onLeader),
        mute.subscribe(onMuted),
        onHandoffAudioUnlock(() => {
          currentLeader.announce();
          onLeader(currentLeader.snapshot());
        }),
        installHandoffAudioUnlock(),
      ];
      // A linha de base recomeça a cada partida.
      seenMarks.clear();
      for (const [id, state] of states) {
        if (state) seenMarks.set(id, state.count > 0 ? state.latestMark : null);
      }
      emit();
      scheduleRepeat(false);
      scheduleMuteEnd();
    },

    stop() {
      if (!started) return;
      started = false;
      for (const cleanup of cleanups) cleanup();
      cleanups = [];
      stopRepeat();
      if (muteTimer !== null) clearTimeout(muteTimer);
      muteTimer = null;
      mute?.stop();
      leader?.stop();
      mute = null;
      leader = null;
      if (baseTitle !== null && typeof document !== 'undefined') document.title = baseTitle;
      baseTitle = null;
    },

    setSourceState(sourceId, state) {
      if (sameState(states.get(sourceId), state)) return;
      states.set(sourceId, state);
      let fresh: AlertSourceState | null = null;
      // Fonte que ainda não sabe (ou se escondeu) não marca linha de base.
      if (state) {
        const mark = state.count > 0 ? state.latestMark : null;
        const known = seenMarks.has(sourceId);
        const highest = seenMarks.get(sourceId) ?? null;
        const passes = mark !== null && (highest === null || mark > highest);
        if (passes || !known) seenMarks.set(sourceId, passes ? mark : highest);
        if (known && passes) fresh = state;
      }
      emit();
      if (fresh && canSound()) {
        play(sourceId);
        if (fresh.notification) notify(fresh.notification);
        scheduleRepeat(true);
      } else {
        scheduleRepeat(false);
      }
    },

    setOpenHandler(next) {
      open = next;
    },

    enableSound() {
      if (!unlockHandoffAudio()) return;
      if (leader) onLeader(leader.snapshot());
      // O clique libera e toca uma vez, para a pessoa saber que funciona.
      play(activeLines()[0]?.sourceId);
    },

    mute() {
      if (!mute) return;
      onMuted(mute.mute());
    },

    async requestNotifications() {
      try {
        permission = await Notification.requestPermission();
      } catch {
        permission = readNotificationPermission();
      }
      emit();
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    getSnapshot: () => snapshot,
  };
}
